import { escapeCsvCell } from "@/shared/utils/csv";

import type { PayoutDestinationSnapshot, RescuePayout } from "../types/payoutRescue.types";

/** Backend money fields are decimal strings at 8 dp — format to 2 dp for display, keep the raw for copy. */
export function formatMoney(value: string | number | null | undefined, locale: string): string {
  const n = Number(value ?? 0);
  return (Number.isFinite(n) ? n : 0).toLocaleString(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/** Whole minutes elapsed since `createdAt` (never negative — clock skew reads as "just now"). */
export function ageMinutes(createdAt: string, now: Date = new Date()): number {
  const created = new Date(createdAt).getTime();
  if (!Number.isFinite(created)) return 0;
  return Math.max(0, Math.floor((now.getTime() - created) / 60_000));
}

/** Compact age label: "38m", "2h 14m", "3d 5h". Computed at render time — no ticking interval. */
export function formatAge(minutes: number): string {
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ${minutes % 60}m`;
  const days = Math.floor(hours / 24);
  return `${days}d ${hours % 24}h`;
}

/** Short display id for a payout uuid: `#f3b1c2d4…0001`. */
export function shortId(id: string): string {
  return `#${id.slice(0, 8)}…${id.slice(-4)}`;
}

/**
 * Masked destination for on-screen display (full value only on explicit copy). CLABE shows bank prefix + last
 * 4; CBU/CVU shows the bank/wallet head + last 4 of the 22 digits; PIX keys mask the middle of the
 * identifier.
 */
export function maskDestination(destination: PayoutDestinationSnapshot): string {
  if (destination.rail === "SPEI") {
    const digits = destination.clabe.replace(/\D/g, "");
    if (digits.length < 10) return "····";
    return `${digits.slice(0, 4)} ${digits.slice(4, 6)} ···· ···· ${digits.slice(-4)}`;
  }
  if (destination.rail === "CBU_CVU") {
    const digits = destination.account_id_value.replace(/\D/g, "");
    if (digits.length < 10) return "····";
    return `${digits.slice(0, 4)} ···· ···· ···· ${digits.slice(-4)}`;
  }
  const value = destination.pix_key_value;
  switch (destination.pix_key_type) {
    case "CPF":
    case "CNPJ":
    case "PHONE": {
      const digits = value.replace(/\D/g, "");
      return digits.length > 4 ? `···· ${digits.slice(-4)}` : "····";
    }
    case "EMAIL": {
      const at = value.indexOf("@");
      return at > 0 ? `${value[0]}···${value.slice(at)}` : "····";
    }
    default:
      // EVP / unknown key types: show only the tail.
      return value.length > 4 ? `···· ${value.slice(-4)}` : "····";
  }
}

/** Raw destination value (what the copy button puts on the clipboard). */
export function destinationValue(destination: PayoutDestinationSnapshot): string {
  if (destination.rail === "SPEI") return destination.clabe;
  if (destination.rail === "CBU_CVU") return destination.account_id_value;
  return destination.pix_key_value;
}

/**
 * Per-rail i18n key (+ interpolation) for the destination row label — the drawer and the retry modal share
 * this, so a new corridor extends ONE place (next to maskDestination/destinationValue).
 */
export function destinationLabel(destination: PayoutDestinationSnapshot): {
  key: "destination_label_clabe" | "destination_label_pix" | "destination_label_cbu_cvu";
  params?: { type: string };
} {
  if (destination.rail === "SPEI") return { key: "destination_label_clabe" };
  if (destination.rail === "CBU_CVU") {
    return { key: "destination_label_cbu_cvu", params: { type: destination.account_id_type } };
  }
  return { key: "destination_label_pix", params: { type: destination.pix_key_type } };
}

/** Beneficiary name if the snapshot carries one (PIX allows creating without it). */
export function destinationBeneficiary(
  destination: PayoutDestinationSnapshot | null | undefined,
): string | null {
  if (!destination) return null;
  return destination.beneficiary_name?.trim() || null;
}

/**
 * CSV export of the currently visible rows (client-side — there is no export endpoint). Money stays the raw
 * decimal strings so the file re-imports losslessly. Never includes provider identity.
 */
export function buildRescueCsv(rows: RescuePayout[], orgName: (id: string) => string): string {
  const header = [
    "id",
    "organization",
    "country",
    "rail",
    "currency",
    "status",
    "gross_amount",
    "fee_total",
    "net_amount",
    "concept",
    "created_at",
  ];
  const escape = escapeCsvCell;
  const lines = rows.map((p) =>
    [
      p.id,
      escape(orgName(p.organization_id)),
      p.country,
      p.rail,
      p.currency,
      p.status,
      p.gross_amount,
      p.fee_total,
      p.net_amount,
      escape(p.concept),
      p.created_at,
    ].join(","),
  );
  return [header.join(","), ...lines].join("\n");
}

// Re-exported so this module keeps one import surface for its page; the implementation is shared with the other
// ops surfaces that export CSV (`@/shared/utils/csv`).
export { downloadCsv } from "@/shared/utils/csv";
